import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import api from '../api';
import { makeApiRequest } from '../utils/apiHelper';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const isFetchingRef = useRef(false);
  const lastFetchKeyRef = useRef('');
  const { token, isLoggedIn } = useAuth();

  // Fetch cart items from the server with retry logic and resource management
  // Memoize with useCallback to prevent infinite loops
  const fetchCartItems = useCallback(async (retryCount = 0) => {
    if (!isLoggedIn || !token) {
      setCartItems([]);
      setCartCount(0);
      return;
    }

    // Prevent multiple simultaneous requests using ref
    if (isFetchingRef.current) {
      //console.log('Cart fetch already in progress, skipping...');
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      
      // Add timeout and retry logic
      const axiosConfig = {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000, // 10 second timeout
      };

      const [cartRes, purchasedRes] = await Promise.all([
        makeApiRequest(() => axios.get(`${api.getCartAndWishlist}`, axiosConfig)),
        makeApiRequest(() => axios.get(`${api.getPurchasedCourses}`, axiosConfig)),
      ]);

      const purchasedIds = purchasedRes.data.courses.map((course) => course._id);
      const filteredCart = cartRes.data.cart.filter(
        (item) => !purchasedIds.includes(item._id),
      );

      setCartItems(filteredCart);
      setCartCount(filteredCart.length);
    } catch (error) {
      console.error('Error fetching cart items:', error);
      
      // Retry logic for network errors
      if (retryCount < 2 && (error.code === 'ERR_NETWORK' || error.code === 'ERR_INSUFFICIENT_RESOURCES')) {
        // console.log(`Retrying cart fetch (attempt ${retryCount + 1})...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
        return fetchCartItems(retryCount + 1);
      }
      
      setCartItems([]);
      setCartCount(0);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [isLoggedIn, token]); // Only include stable dependencies

  // Add item to cart - memoized to prevent recreation
  const addToCart = useCallback(async (courseId) => {
    if (!isLoggedIn || !token) {
      throw new Error('User must be logged in to add items to cart');
    }

    try {
      await axios.post(
        `${api.addToCart}`,
        { courseId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      
      // Refresh cart items after adding
      await fetchCartItems();
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }, [isLoggedIn, token, fetchCartItems]);

  // Remove item from cart - memoized to prevent recreation
  const removeFromCart = useCallback(async (courseId) => {
    if (!isLoggedIn || !token) {
      throw new Error('User must be logged in to remove items from cart');
    }

    try {
      await axios.delete(`${api.addToCart}/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Refresh cart items after removing
      await fetchCartItems();
      return true;
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  }, [isLoggedIn, token, fetchCartItems]);

  // Clear entire cart - memoized to prevent recreation
  const clearCart = useCallback(() => {
    setCartItems([]);
    setCartCount(0);
  }, []);

  // Check if item is in cart - memoized with cartItems dependency
  const isInCart = useCallback((courseId) => {
    return cartItems.some(item => item._id === courseId);
  }, [cartItems]);

  // Get cart total price - memoized with cartItems dependency
  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => {
      const price = item.offerPrice !== -1 ? item.offerPrice : item.actualPrice;
      return total + price;
    }, 0);
  }, [cartItems]);

  // Get cart total for selected items - memoized with cartItems dependency
  const getSelectedCartTotal = useCallback((selectedCourseIds) => {
    return cartItems
      .filter(item => selectedCourseIds.includes(item._id))
      .reduce((total, item) => {
        const price = item.offerPrice !== -1 ? item.offerPrice : item.actualPrice;
        return total + price;
      }, 0);
  }, [cartItems]);

  // Fetch cart items when user logs in or token changes.
  // Including fetchCartItems is safe because it's memoized by [isLoggedIn, token]
  // and won't change on the re-renders caused by setting cart state.
  useEffect(() => {
    const fetchKey = isLoggedIn && token ? `in:${token}` : 'out';
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;

    if (!isLoggedIn || !token) {
      setCartItems((prev) => (prev.length ? [] : prev));
      setCartCount((prev) => (prev ? 0 : prev));
      return;
    }

    fetchCartItems();
  }, [isLoggedIn, token, fetchCartItems]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    cartItems,
    cartCount,
    loading,
    fetchCartItems,
    addToCart,
    removeFromCart,
    clearCart,
    isInCart,
    getCartTotal,
    getSelectedCartTotal,
  }), [cartItems, cartCount, loading, fetchCartItems, addToCart, removeFromCart, clearCart, isInCart, getCartTotal, getSelectedCartTotal]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;

